import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SheetMirrorColumnDto,
  SheetMirrorEditDto,
  SheetMirrorEditResult,
  SheetMirrorRowDto,
  SheetMirrorTabDto,
  SheetMirrorTabKey,
} from '@bhops/shared';
import { sheetMirrorApi } from '@/api/sheetMirror';
import { extractApiError } from '@/api/client';
import { useToast } from '@/components/Toast';
import type { SheetGridRow } from './SheetGrid';
import { dateLike, isoFromText } from './sheetDates';

// 시트 거울 탭을 칸에서 고치는 화면의 공통 뼈대(2026-10-23) — 데모-계약 전환 · 도입 문의 현황이 같이 쓴다.
//   · 탭을 받아 오고(쓰는 중이면 10초 · 아니면 1분마다), 줄마다 아직 시트에 안 쓴 값 · 못 쓴 편집을 모은다.
//   · 칸 하나 저장(고치기 전 값 = 쓰는 중인 값 ?? 시트 글자) · 새 줄 작성(draft) · 추가.
//   · 표 줄(SheetGridRow) 만들기 — 쓰는 중인 칸은 기울임, 못 쓴 줄은 빨강.
//   · 되돌리기(Ctrl+Z) · 다시 하기(Ctrl+Y) — 이 화면에서 고친 칸을 고치기 전 값으로 다시 보낸다.

const OPEN = new Set(['PENDING', 'SENT']);
/** 작성 중인 새 줄의 열쇠 */
export const DRAFT = '__draft__';

/** 되돌리기 한 걸음 — 한 줄의 칸들을 before → after 로 고쳤다(값은 고칠 때 쓰는 모양, 날짜 'YYYY-MM-DD') */
type Step = { row: string; before: Record<string, string>; after: Record<string, string> };

export function useSheetMirrorEditor(tabKey: SheetMirrorTabKey | null) {
  const qc = useQueryClient();
  const toast = useToast();
  const qk = useMemo(() => ['sheet-mirror', tabKey] as const, [tabKey]);

  const q = useQuery({
    queryKey: qk,
    queryFn: () => sheetMirrorApi.get(tabKey!),
    enabled: !!tabKey,
    // 시트에 쓰는 중이면 10초마다, 아니면 1분마다(시트 쪽 변경을 받아 온다).
    refetchInterval: (query) =>
      (query.state.data as SheetMirrorTabDto | undefined)?.edits.some((e) => OPEN.has(e.status))
        ? 10_000
        : 60_000,
    retry: false,
  });
  const tab = q.data;

  /** 작성 중인 새 줄(칸 열쇠 → 값). [시트에 추가] 를 눌러야 서버로 간다. 탭을 바꾸면 버린다. */
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  /**
   * 되돌리기 · 다시 하기 쌓기(2026-10-23 오너 "CTRL + Z 누르면 수정사항 되돌리기, CTRL + Y 누르면 되돌리기 취소").
   * 이 화면에서 고친 것만 — 시트에서 다른 사람이 고친 것은 되돌리지 않는다. 탭을 바꾸면 비운다.
   */
  const undoStack = useRef<Step[]>([]);
  const redoStack = useRef<Step[]>([]);
  useEffect(() => {
    setDraft(null);
    undoStack.current = [];
    redoStack.current = [];
  }, [tabKey]);

  /** 줄마다 아직 시트에 안 쓴 값(먼저 고친 것 → 나중, 나중 것이 이긴다) */
  const openByRow = useMemo(() => {
    const m = new Map<string, Record<string, string>>();
    for (const e of [...(tab?.edits ?? [])].reverse()) {
      if (!e.rowKey || !OPEN.has(e.status)) continue;
      m.set(e.rowKey, { ...(m.get(e.rowKey) ?? {}), ...e.changes });
    }
    return m;
  }, [tab]);
  /**
   * 아직 시트에 안 쓴 값을 얹은 줄 — 정렬은 화면에 보이는 값으로(2026-10-23 오너 "주차는 정렬이 안되네":
   * 방금 고친 4주차가 시트의 옛 값 1 로 정렬됐다). 정렬 기준인 raw 에만 얹는다 — cells(시트에 보이는 글자)에
   * '2026-10-03' 을 얹으면 날짜 열 모양을 '2026. 10. 3' 으로 잘못 알아봤다(오너 "왜 연도까지 붙어서").
   */
  const withPending = useCallback(
    (rows: SheetMirrorRowDto[]): SheetMirrorRowDto[] =>
      rows.map((r) => {
        const open = openByRow.get(r.rowKey);
        return open ? { ...r, raw: { ...r.raw, ...open } } : r;
      }),
    [openByRow],
  );
  /** 줄의 가장 나중 편집이 시트에 못 쓴 것이면 그 편집 */
  const badByRow = useMemo(() => {
    const m = new Map<string, SheetMirrorEditDto>();
    const seen = new Set<string>();
    for (const e of tab?.edits ?? []) {
      if (!e.rowKey || seen.has(e.rowKey)) continue;
      seen.add(e.rowKey);
      if (e.status === 'CONFLICT' || e.status === 'FAILED') m.set(e.rowKey, e);
    }
    return m;
  }, [tab]);
  const pendingNew = useMemo(
    () => (tab?.edits ?? []).filter((e) => !e.rowKey && OPEN.has(e.status)),
    [tab],
  );
  const problems = useMemo(
    () => (tab?.edits ?? []).filter((e) => e.status === 'CONFLICT' || e.status === 'FAILED'),
    [tab],
  );

  /** 서버가 돌려준 편집을 화면 자료에 바로 넣는다(다시 받기 전에도 '쓰는 중'이 보이게). */
  const putEdit = (res: SheetMirrorEditResult) => {
    const edit = res.edit;
    if (!edit) return;
    qc.setQueryData<SheetMirrorTabDto>(qk, (old) =>
      old ? { ...old, edits: [edit, ...old.edits.filter((e) => e.id !== edit.id)] } : old,
    );
  };

  const sheetRowOf = (key: string) =>
    key === DRAFT ? undefined : tab?.rows.find((r) => r.rowKey === key);
  /** 보냈는데 아직 답이 안 온 값 — 답을 기다리지 않고 누른 Ctrl+Z · Ctrl+Y 도 그 값을 '지금 값'으로 */
  const inflight = useRef(new Map<string, Record<string, string>>());
  const settled = useRef(new Set<string>());
  // 답이 온 값은 화면 자료가 바뀐 뒤(쓰는 중인 값으로 보인 뒤)에 내려놓는다
  useEffect(() => {
    for (const id of settled.current) {
      const [key, k] = [id.slice(0, id.indexOf('|')), id.slice(id.indexOf('|') + 1)] as const;
      const rest = { ...inflight.current.get(key) };
      delete rest[k];
      if (Object.keys(rest).length) inflight.current.set(key, rest);
      else inflight.current.delete(key);
    }
    settled.current.clear();
  }, [tab]);
  // 서버가 보는 '지금 값'과 같게 — 보내는 중 · 쓰는 중인 값이 있으면 그것, 없으면 시트 글자
  const nowOf = (key: string, k: string) =>
    inflight.current.get(key)?.[k] ?? openByRow.get(key)?.[k] ?? sheetRowOf(key)?.cells[k] ?? '';
  /** 되돌릴 때 보낼 '고치기 전 값' — 쓰는 중인 값, 없으면 시트 칸을 고칠 때 쓰는 모양으로 */
  const beforeOf = (key: string, k: string) => {
    if (key === DRAFT) return draft?.[k] ?? '';
    const open = inflight.current.get(key)?.[k] ?? openByRow.get(key)?.[k];
    if (open !== undefined) return open;
    const r = sheetRowOf(key);
    const col = tab?.columns.find((x) => x.key === k);
    return r && col ? editValue(col, r) : (r?.cells[k] ?? '');
  };

  /** 한 줄의 칸들을 보낸다(새 줄 작성 중이면 그 줄에 넣기만). */
  const send = async (key: string, changes: Record<string, string>) => {
    if (key === DRAFT) {
      setDraft((d) => ({ ...(d ?? {}), ...changes }));
      return true;
    }
    if (!tabKey || !sheetRowOf(key)) return false;
    const expected = Object.fromEntries(Object.keys(changes).map((k) => [k, nowOf(key, k)]));
    inflight.current.set(key, { ...inflight.current.get(key), ...changes });
    try {
      putEdit(await sheetMirrorApi.update(tabKey, key, changes, expected));
      return true;
    } catch (err) {
      toast.error(extractApiError(err), '저장 실패');
      await qc.invalidateQueries({ queryKey: qk });
      return false;
    } finally {
      // 답이 왔다 — 화면 자료에 들어간 뒤(아래 effect) 내려놓는다. 그 뒤에 또 보낸 값은 그대로.
      for (const [k, v] of Object.entries(changes))
        if (inflight.current.get(key)?.[k] === v) settled.current.add(`${key}|${k}`);
    }
  };

  /**
   * 칸 저장. also: 같은 줄의 곁 칸도 함께(문의 일자 → 주차 · 요일) — 수식 칸 · 이미 같은 값은 뺀다.
   */
  const commit = async (
    row: SheetGridRow,
    c: SheetMirrorColumnDto,
    value: string,
    also?: Record<string, string>,
  ) => {
    const sheetRow = sheetRowOf(row.key);
    const changes: Record<string, string> = { [c.key]: value };
    for (const [k, v] of Object.entries(also ?? {}))
      if (!sheetRow?.formulas.includes(k) && (row.key === DRAFT || nowOf(row.key, k) !== v))
        changes[k] = v;
    const before = Object.fromEntries(Object.keys(changes).map((k) => [k, beforeOf(row.key, k)]));
    // 답을 기다리지 않고 쌓는다 — 고치자마자 누른 Ctrl+Z 도 먹게(못 보내면 뺀다)
    const step: Step = { row: row.key, before, after: changes };
    undoStack.current.push(step);
    redoStack.current = [];
    const ok = await send(row.key, changes);
    if (!ok) undoStack.current = undoStack.current.filter((s) => s !== step);
    return ok;
  };

  /**
   * Ctrl+Z — 가장 나중에 고친 것을 고치기 전 값으로 다시 보낸다. 서버 답을 기다리지 않고 걸음을 옮겨
   * 잇달아 누른 Ctrl+Z · Ctrl+Y 도 차례대로 먹는다(못 보내면 제자리로).
   */
  const undo = async () => {
    const step = undoStack.current.pop();
    if (!step) return;
    redoStack.current.push(step);
    if (await send(step.row, step.before)) return;
    redoStack.current = redoStack.current.filter((s) => s !== step);
    undoStack.current.push(step);
  };
  /** Ctrl+Y — 되돌린 것을 다시. */
  const redo = async () => {
    const step = redoStack.current.pop();
    if (!step) return;
    undoStack.current.push(step);
    if (await send(step.row, step.after)) return;
    undoStack.current = undoStack.current.filter((s) => s !== step);
    redoStack.current.push(step);
  };

  const addMx = useMutation({
    mutationFn: (values: Record<string, string>) => sheetMirrorApi.append(tabKey!, values),
    onSuccess: (res) => {
      putEdit(res);
      setDraft(null);
      // 새 줄 작성 중에 고친 걸음은 그 줄과 함께 시트로 갔다
      undoStack.current = undoStack.current.filter((s) => s.row !== DRAFT);
      redoStack.current = redoStack.current.filter((s) => s.row !== DRAFT);
      toast.success('새 줄을 넣었습니다. 1분 안에 시트의 마지막 줄 다음에 쓰입니다.');
    },
    onError: (err) => toast.error(extractApiError(err), '새 줄 추가 실패'),
  });

  /**
   * 표 줄 — 작성 중인 새 줄 · 쓰는 중인 새 줄 · 시트 줄(쓰는 중인 값을 얹어서).
   * more: 줄마다 덧붙일 것(줄 색 · 강조 등) — 고치는 중인 값(values)으로 따진다.
   */
  const buildRows = useCallback(
    (
      rows: SheetMirrorRowDto[],
      gridCols: SheetMirrorColumnDto[],
      more?: (r: SheetMirrorRowDto, values: Record<string, string>) => Partial<SheetGridRow>,
    ): SheetGridRow[] => {
      const out: SheetGridRow[] = [];
      // 날짜 칸의 대기 값은 그 열에 보이는 모양대로('10월21일') — 열마다 보기 하나
      const samples = dateSamples(rows, gridCols);
      const fmt = (c: SheetMirrorColumnDto, v: string) => pendingText(c, v, samples[c.key]);
      const show = (vals: Record<string, string>) =>
        Object.fromEntries(gridCols.map((c) => [c.key, fmt(c, vals[c.key] ?? '')]));
      if (draft)
        out.push({
          key: DRAFT,
          cells: show(draft),
          values: draft,
          tone: 'draft',
          title: '칸을 채우고 [시트에 추가]를 눌러 주세요',
          testId: 'sheet-row-new',
        });
      for (const e of pendingNew)
        out.push({
          key: `edit:${e.id}`,
          cells: show(e.changes),
          values: e.changes,
          lockedRow: true,
          pending: new Set(Object.keys(e.changes)),
          tone: 'pending',
          title: '새 줄을 시트에 쓰는 중…',
        });
      for (const r of rows) {
        const open = openByRow.get(r.rowKey) ?? {};
        const bad = badByRow.get(r.rowKey);
        const values = Object.fromEntries(gridCols.map((c) => [c.key, editValue(c, r)]));
        const cells = { ...r.cells };
        for (const [k, v] of Object.entries(open)) {
          const c = gridCols.find((x) => x.key === k);
          values[k] = v;
          cells[k] = c ? fmt(c, v) : v;
        }
        out.push({
          key: r.rowKey,
          cells,
          values,
          bg: r.bg,
          locked: new Set(r.formulas),
          pending: new Set(Object.keys(open)),
          tone: bad && !Object.keys(open).length ? 'problem' : undefined,
          title: bad ? `시트에 쓰지 못했습니다. ${bad.message ?? ''}` : undefined,
          testId: `sheet-row-${r.rowNumber}`,
          ...more?.(r, values),
        });
      }
      return out;
    },
    [draft, pendingNew, openByRow, badByRow],
  );

  return {
    q,
    tab,
    draft,
    setDraft,
    problems,
    commit,
    addMx,
    buildRows,
    withPending,
    undo,
    redo,
  };
}

/** 날짜 칸의 대기 값('2026-10-23')을 시트 모양에 가깝게('10. 23'). */
export function pendingText(col: SheetMirrorColumnDto, v: string, sample?: string): string {
  return col.type === 'date' ? dateLike(v, sample) : v;
}

/** 날짜 칸마다 시트에 보이는 글자 하나(실제 날짜가 있는 줄에서) — 대기 값을 같은 모양으로 보이려고 */
export function dateSamples(
  rows: readonly SheetMirrorRowDto[],
  cols: readonly SheetMirrorColumnDto[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const c of cols) {
    if (c.type !== 'date') continue;
    // 시트에 보이는 글자만 보기로(쓰는 중인 'YYYY-MM-DD' 는 빼고)
    const r = rows.find(
      (x) =>
        typeof x.raw[c.key] === 'string' &&
        (x.cells[c.key] ?? '').trim() &&
        !/^\d{4}-\d{2}-\d{2}$/.test(x.cells[c.key]!.trim()),
    );
    if (r) out[c.key] = r.cells[c.key]!;
  }
  return out;
}

/** 칸을 고칠 때 시작 값 — 날짜 칸은 실제 날짜('YYYY-MM-DD'), 숫자 칸은 숫자, 그 밖은 시트 글자. */
export function editValue(col: SheetMirrorColumnDto, row: SheetMirrorRowDto): string {
  const raw = row.raw[col.key];
  // 날짜 칸 — 실제 날짜, 없으면 글자로 쓴 날짜('10.12', 시연 예정 같은 칸)를 읽어 달력을 그 날로
  if (col.type === 'date')
    return typeof raw === 'string' ? raw : isoFromText(row.cells[col.key] ?? '');
  if (col.type === 'number')
    return raw !== undefined ? String(raw) : (row.cells[col.key] ?? '').replace(/,/g, '');
  return row.cells[col.key] ?? '';
}

/** 못 쓴 편집 한 줄 설명 — "'상호' 칸 · 칸" */
export function describeEdit(e: SheetMirrorEditDto, tab: SheetMirrorTabDto): string {
  const nameOf = (cells: Record<string, string>) => cells['상호'] || cells['기업/상호'] || '';
  const row = e.rowKey ? tab.rows.find((r) => r.rowKey === e.rowKey) : null;
  const name = row ? nameOf(row.cells) || `${row.rowNumber}행` : nameOf(e.changes) || '새 줄';
  return `'${name}' ${Object.keys(e.changes).join(', ')}`;
}

export function agoText(iso: string): string {
  const min = Math.floor((Date.now() - Date.parse(iso)) / 60_000);
  if (min < 1) return '방금';
  if (min < 60) return `${min}분 전`;
  const h = Math.floor(min / 60);
  return h < 24 ? `${h}시간 전` : `${Math.floor(h / 24)}일 전`;
}
